import { TextField, IconButton, InputAdornment } from "@mui/material";
import React, { useEffect, useRef } from "react";
import "./AgencySearchAddress.scss";
import SearchIcon from "../../../components/icons/SearchIcon";

const extractHouseNumber = (
  components: Record<string, string>,
  formattedAddress?: string,
): string => {
  if (components.subpremise && components.street_number) {
    return `${components.subpremise}/${components.street_number}`;
  }

  if (components.street_number) {
    return components.street_number;
  }

  if (formattedAddress) {
    const match = formattedAddress.match(/^([\w\-\/]+)/);
    if (match) {
      return match[1];
    }
  }

  return "";
};

const AgencySearchAddress = ({ formik }: { formik: any }) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!window.google || !searchInputRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(
      searchInputRef.current,
      {
        types: ["address"],
        componentRestrictions: { country: ["au", "nz"] },
        fields: ["address_components", "formatted_address"],
      },
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.address_components) return;

      const components: Record<string, string> = {
        subpremise: "",
        street_number: "",
        route: "",
        locality: "",
        administrative_area_level_1: "",
        country: "",
        postal_code: "",
      };

      place.address_components.forEach((component: any) => {
        const type = component.types[0];
        if (components[type] !== undefined) {
          components[type] = component.long_name;
        }
      });

      let unit = components.subpremise;
      let house = components.street_number;

      if (!unit && place.formatted_address) {
        const unitMatch = place.formatted_address.match(
          /^([\w\d]+)\/([\w\d]+)/,
        );
        if (unitMatch) {
          unit = unitMatch[1];
          house = unitMatch[2];
        }
      }

      formik.setFieldValue("searchAddress", place.formatted_address);
      formik.setFieldValue("unitLevel", unit);
      formik.setFieldValue("houseNo", house);
      formik.setFieldValue("streetName", `${components.route}`);
      formik.setFieldValue("city", components.locality);
      formik.setFieldValue("state", components.administrative_area_level_1);
      formik.setFieldValue("country", components.country);
      formik.setFieldValue("postCode", components.postal_code);
    });

    return () => {
      window.google?.maps?.event?.clearInstanceListeners(autocomplete);
    };
  }, []);

  return (
    <div className="search-container">
      <div
        style={{
          position: "absolute",
          left: "-9999px",
          opacity: 0,
          height: 0,
          pointerEvents: "none",
        }}
      >
        <input type="text" name="address" autoComplete="off" tabIndex={-1} />
        <input type="text" name="street" autoComplete="off" tabIndex={-1} />
      </div>

      <TextField
        name="searchAddress"
        inputRef={searchInputRef}
        variant="outlined"
        placeholder="Search Address"
        className="search-field"
        value={formik.values.searchAddress}
        onChange={(e) => {
          formik.handleChange(e);
          (e.target as HTMLInputElement).setAttribute(
            "autocomplete",
            `disabled-${Math.random()}`,
          );
        }}
        error={!!formik.errors.searchAddress}
        autoComplete="off"
        inputProps={{
          readOnly: true,
          onFocus: (e) => {
            const input = e.target as HTMLInputElement;
            input.removeAttribute("readonly");
            input.name = "searchAddress";
            input.setAttribute("autocomplete", "new-password");
          },
          onBlur: (e) => {
            (e.target as HTMLInputElement).name = "manual-address-search";
          },
          onInput: (e) => {
            (e.target as HTMLInputElement).setAttribute(
              "autocomplete",
              `no-autofill-${Date.now()}`,
            );
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};

export default AgencySearchAddress;
